import React, {useMemo} from 'react';

import {ErrorBoundary} from '@sentry/react';
import {StepHeader} from 'src/components/step-header';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {Group, Item} from '@pb/kit-component-group';
import {StickyElement} from '@pb/kit-component-sticky-element';

import {STEP_TITLE} from '../../constants';
import {CreateUserProgressBar} from '../progress-bar';

import {
    createUser,
    createUserHeader,
    createUserTitle,
} from './create-user.module.styl';

import type {CREATE_USER_STEPS} from '../../constants';
import type {ReactNode} from 'react';
import type {Error} from 'src/openapi/admin/models/Error';


type Props = {
    content: ReactNode,
    loadingError: Error | null,
    selectedStep: CREATE_USER_STEPS,
    buttonsBlock: ReactNode[],
    canViewLimits: boolean,
    canSetupAdminRole: boolean,
    onChangeStep?: (step: CREATE_USER_STEPS) => void,
};

export const CreateUserStep = ({
    content,
    loadingError,
    selectedStep,
    buttonsBlock,
    canViewLimits,
    canSetupAdminRole,
    onChangeStep,
}: Props) => {
    const loadedContent = useMemo(() => {
        if (loadingError && loadingError.status === 403) {
            return <ErrorPage handler="/" title="Access Denied" subtitle="You have no access to this page"/>;
        } else if (loadingError) {
            return <ErrorPage title="Service is temporarily unavailable" subtitle="Please try later"/>;
        } else {
            return content;
        }
    }, [loadingError, content]);

    return (
        <ErrorBoundary>
            <div className={createUser}>
                <StickyElement className={createUserHeader}>
                    <div className={createUserTitle}>
                        <StepHeader title="Add User" subTitle={STEP_TITLE[selectedStep]}/>
                        <Group>
                            {buttonsBlock}
                        </Group>
                    </div>
                    <div className="pd-m">
                        <CreateUserProgressBar {...{
                            selectedStep,
                            canSetupAdminRole,
                            canViewLimits,
                            onChangeStep,
                        }}
                        />
                    </div>
                </StickyElement>
                <Item grow={1} className="pd-l">
                    <Group direction="v" gap="xxl" alignItems="start">
                        {loadedContent}
                    </Group>
                </Item>
            </div>
        </ErrorBoundary>
    );
};
